import React, { CSSProperties, KeyboardEventHandler, MouseEventHandler, ReactNode } from 'react';

// Components
import { SVGIcon } from '../../atoms/SVG';
import { Hyperlink } from '../../atoms/Hyperlink';
import { Tooltip, TooltipPlacement } from '../../atoms/Tooltip';
import { useBtn } from './useBtn';
import { ButtonLink } from './Button/ButtonLink';
import { BtnModifier } from './Btn.modifier';
import Bem from '../../../lib/bemUtils/bem';

export type ButtonRefType = React.RefObject<HTMLButtonElement | HTMLAnchorElement | null>;

export type BtnType = {
  tabIndex?: number;
  focusOnMount?: boolean;
  tooltipPlacement?: TooltipPlacement;
  cssClass?: string;
  svgClass?: string;
  /**
   * modifier: eg. btn--big
   */
  modifier?: BtnModifier | BtnModifier[];
  href?: string;
  rel?: string;
  disabled?: boolean;
  disabledLink?: boolean;
  text?: string;
  children?: ReactNode;
  titleText?: string;
  hiddenText?: boolean;
  svgSpriteId?: SVGIcon;
  type?: 'button' | 'submit' | 'reset' | undefined;
  target?: string;
  role?: 'button' | 'link';
  ariaExpanded?: boolean;
  ariaHaspopup?: boolean;
  ariaControls?: string | string[] | undefined;
  ariaCurrent?: boolean | 'false' | 'true' | 'page' | 'step' | 'location' | 'date' | 'time' | undefined;
  ariaPressed?: boolean;
  ariaDisabled?: boolean;
  ariaDescribedBy?: string;
  ariaLabel?: string;
  routing?: boolean;
  id?: string;
  formAction?: string;
  value?: string;
  name?: string;
  style?: CSSProperties | undefined;
  reference?: ButtonRefType;
  // Events
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onKeyDown?: KeyboardEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onKeyUp?: KeyboardEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  accessKey?: string;
};

// should be converted to forward ref
export default function Btn({
  tabIndex,
  focusOnMount = false,
  tooltipPlacement = TooltipPlacement.BOTTOM,
  cssClass,
  svgClass,
  modifier,
  href,
  rel,
  disabled = false,
  disabledLink = false,
  text,
  children,
  titleText,
  hiddenText = false,
  svgSpriteId,
  type = 'button',
  target,
  role,
  ariaExpanded,
  ariaHaspopup,
  ariaControls,
  ariaCurrent,
  ariaPressed,
  ariaDisabled,
  ariaDescribedBy,
  ariaLabel,
  routing = true,
  id,
  formAction,
  value,
  name,
  style,
  reference,
  onClick,
  onKeyDown,
  onKeyUp,
  accessKey,
}: BtnType) {
  const { ref, hideTooltipFromScreenReader, getText, getIcon, cssClasses } = useBtn(
    reference,
    focusOnMount,
    hiddenText,
    modifier,
    cssClass,
    text,
    svgSpriteId,
    svgClass,
    children,
  );

  if (disabledLink) {
    return (
      <ButtonLink
        ariaCurrent={ariaCurrent}
        cssClasses={cssClasses}
        ref={ref as React.RefObject<HTMLAnchorElement>}
        titleText={titleText}
      >
        {getIcon()}
        {getText()}
      </ButtonLink>
    );
  }

  const parseAriaControls = (): string | undefined => {
    if (Array.isArray(ariaControls)) {
      return ariaControls.join(' ');
    }
    return ariaControls;
  };

  // do we have an <a> or a real <button> ? => depends on the href attribute!
  if (href) {
    return (
      // if used correctly and wisely, accesskeys are a11y enhancement
      // eslint-disable-next-line jsx-a11y/no-access-key
      <Hyperlink
        reference={ref}
        role={role}
        ariaExpanded={ariaExpanded}
        ariaControls={parseAriaControls()}
        aria-current={ariaCurrent}
        ariaDescribedBy={ariaDescribedBy}
        ariaHaspopup={ariaHaspopup}
        ariaLabel={ariaLabel}
        tooltipPlacement={tooltipPlacement}
        to={href}
        rel={rel}
        routing={routing}
        className={cssClasses}
        title={titleText}
        target={target}
        id={id}
        hideTooltipFromScreenReader={hideTooltipFromScreenReader()}
        onClick={onClick}
        style={style}
        accessKey={accessKey}
        dataIcon={svgSpriteId}
      >
        {getIcon()}
        {getText()}
      </Hyperlink>
    );
  }

  const buttonClasses = (): string => {
    if (cssClasses) {
      return cssClasses;
    }
    return Bem('btn', modifier);
  };

  return (
    <Tooltip attachTo={ref} placement={tooltipPlacement} hideTooltipFromScreenReader={hideTooltipFromScreenReader()}>
      {/* if used correctly and wisely, accesskeys are a11y enhancement */}
      {/* eslint-disable-next-line jsx-a11y/no-access-key */}
      <button
        ref={ref as React.RefObject<HTMLButtonElement>}
        className={buttonClasses()}
        title={titleText}
        // eslint-disable-next-line react/button-has-type
        type={ariaDisabled ? 'button' : type}
        disabled={disabled || undefined}
        role={role}
        aria-expanded={ariaExpanded}
        aria-controls={parseAriaControls()}
        aria-pressed={ariaPressed}
        aria-disabled={ariaDisabled || undefined}
        aria-haspopup={ariaHaspopup}
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabel}
        onClick={!ariaDisabled ? onClick : undefined}
        onKeyDown={!ariaDisabled ? onKeyDown : undefined}
        onKeyUp={!ariaDisabled ? onKeyUp : undefined}
        id={id}
        formAction={formAction}
        value={value}
        name={name}
        style={style}
        tabIndex={tabIndex}
        accessKey={accessKey}
        data-icon={svgSpriteId || null}
      >
        {getIcon()}
        {getText()}
      </button>
    </Tooltip>
  );
}
